<template>
  <div class="dashboard-body">
    <div class="row">
      <sidenav class="custom-col-width-2" />
      <div class="custom-col-width-10">
        <navbar />
        <div class="content-section">
          <section class="list-page">
            <header class="list-page__head">
              <div class="list-page__heading">
                <h2 class="list-page__title">Payments</h2>
                <p class="list-page__count">{{ countLabel }}</p>
              </div>
              <button type="button" class="list-page__action" :disabled="isLoading" v-on:click="loadPayments">
                <i :class="['pi', isLoading ? 'pi-spin pi-spinner' : 'pi-refresh']"></i>
                <span>Refresh</span>
              </button>
            </header>

            <!-- Stats -->
            <div class="stats-grid">
              <article class="stat-card">
                <div class="stat-card-top">
                  <span class="stat-icon green"><i class="pi pi-wallet"></i></span>
                  <span class="stat-chip">collected</span>
                </div>
                <h2 class="amount">{{ money(totalCollected) }}</h2>
                <p>Total Collected</p>
                <span class="stat-sub">{{ fmt(collectedCount) }} successful payment{{ collectedCount === 1 ? '' : 's' }}</span>
              </article>

              <article class="stat-card">
                <div class="stat-card-top">
                  <span class="stat-icon indigo"><i class="pi pi-receipt"></i></span>
                  <span class="stat-chip">{{ rangeChip }}</span>
                </div>
                <h2>{{ fmt(totalCount) }}</h2>
                <p>Total Payments</p>
                <span class="stat-sub">{{ rangeLabel }}</span>
              </article>

              <article class="stat-card">
                <div class="stat-card-top">
                  <span class="stat-icon amber"><i class="pi pi-clock"></i></span>
                  <span v-if="pendingCount" class="stat-chip">awaiting</span>
                </div>
                <h2>{{ fmt(pendingCount) }}</h2>
                <p>Pending</p>
                <span class="stat-sub">{{ money(pendingAmount) }} pending</span>
              </article>

              <article class="stat-card">
                <div class="stat-card-top">
                  <span class="stat-icon red"><i class="pi pi-times-circle"></i></span>
                </div>
                <h2>{{ fmt(failedCount) }}</h2>
                <p>Failed / Cancelled</p>
                <span class="stat-sub">unsuccessful payments</span>
              </article>
            </div>

            <!-- Filters -->
            <div class="payments-filter">
              <div class="filter-field">
                <label>Status</label>
                <select v-model="statusFilter" class="form-control shadow-none" v-on:change="onStatusFilterChange">
                  <option value="">All statuses</option>
                  <option v-for="status in statusOptions" :key="status" :value="status">{{ statusLabel(status) }}</option>
                </select>
              </div>
              <div class="filter-field">
                <label>From</label>
                <input type="date" v-model="startDate" :max="endDate || undefined" class="form-control shadow-none" />
              </div>
              <div class="filter-field">
                <label>To</label>
                <input type="date" v-model="endDate" :min="startDate || undefined" class="form-control shadow-none" />
              </div>
              <button type="button" class="filter-apply" :disabled="!canApply || isLoading" v-on:click="applyFilter">
                <i class="pi pi-filter"></i> Apply
              </button>
              <button v-if="hasFilter || statusFilter" type="button" class="filter-clear" :disabled="isLoading" v-on:click="clearFilter">
                Clear
              </button>
            </div>

            <div class="list-page__table">
              <tableLoader v-if="isLoading" message="Loading payments…" />
              <div v-if="!isLoading" class="table-scroller">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Account</th>
                      <th scope="col">Plan</th>
                      <th scope="col">Amount</th>
                      <th scope="col">Method</th>
                      <th scope="col">Reference</th>
                      <th scope="col">Status</th>
                      <th scope="col">Date</th>
                      <th scope="col" class="col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(payment, index) in pagedPayments" v-bind:key="payment.id || index">
                      <th scope="row">{{ rowNumber(index) }}</th>
                      <td class="row-name">{{ accountName(payment) }}</td>
                      <td>{{ payment.subscriptionPlan ? payment.subscriptionPlan.name : '—' }}</td>
                      <td class="amount-cell">{{ money(payment.amount, payment.currency) }}</td>
                      <td>{{ payment.paymentMethod ? payment.paymentMethod.name : '—' }}</td>
                      <td><span class="ref-text">{{ payment.txRef || payment.transactionReference || '—' }}</span></td>
                      <td><span class="badge" :class="statusBadgeClass(payment.status)">{{ statusLabel(payment.status) }}</span></td>
                      <td>{{ $filters.getDateOnly(payment.paymentDate || payment.createdAt) }}</td>
                      <td class="col-actions">
                        <button type="button" class="view-btn" v-on:click="viewPayment(payment)">View</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="!isLoading && filteredPayments.length === 0" class="list-page__empty">
                <i class="pi pi-receipt"></i>
                <div>{{ emptyMessage }}</div>
              </div>
            </div>

            <pagination
              v-if="!isLoading && filteredPayments.length"
              :meta-data="metaData"
              :current-page="currentPage"
              :per-page="perPage"
              v-on:change="onPageChange"
            />
          </section>
        </div>
      </div>
    </div>

    <paymentDetailModal
      v-if="showDetailModal && selectedPayment"
      :payment="selectedPayment"
      @close="showDetailModal = false"
    />
  </div>
</template>
<script>
import sidenav from "@/components/Sidenav.vue";
import navbar from "@/components/Navbar.vue";
import tableLoader from "@/components/Table_Loader.vue";
import pagination from "@/components/Pagination.vue";
import paymentDetailModal from "@/components/payment/Payment_Detail_Modal.vue";
import Payment from "@/services/payment";
import { PAYMENT_STATUS } from "@/utils/enums";
import { DEFAULT_CURRENCY, TOAST_GLOBAL_CONFIG } from "@/utils/const";

const STATUS_BADGE = {
  [PAYMENT_STATUS.success]: "badge-success",
  [PAYMENT_STATUS.pending]: "badge-warning",
  [PAYMENT_STATUS.failed]: "badge-danger",
  [PAYMENT_STATUS.cancelled]: "badge-secondary",
};

export default {
  title: "Payments",
  components: {
    sidenav,
    navbar,
    tableLoader,
    pagination,
    paymentDetailModal,
  },
  data() {
    return {
      payments: [],
      isLoading: false,
      currentPage: 1,
      perPage: 30,
      selectedPayment: null,
      showDetailModal: false,
      // The date range bound to the inputs (YYYY-MM-DD).
      startDate: "",
      endDate: "",
      // The range actually applied to the loaded data (for the stat labels).
      appliedStart: "",
      appliedEnd: "",
      // Status refinement applied client-side to the table (stats stay a full
      // breakdown of the date range).
      statusFilter: "",
      statusOptions: [
        PAYMENT_STATUS.success,
        PAYMENT_STATUS.pending,
        PAYMENT_STATUS.failed,
        PAYMENT_STATUS.cancelled,
      ],
    };
  },
  computed: {
    // The table view: the loaded range, optionally narrowed to one status.
    filteredPayments() {
      if (!this.statusFilter) return this.payments;
      return this.payments.filter((p) => p.status === this.statusFilter);
    },
    // Payments are loaded in full for the active range so the stats are exact;
    // the table itself is paginated client-side.
    pagedPayments() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredPayments.slice(start, start + this.perPage);
    },
    metaData() {
      const total = this.filteredPayments.length;
      return {
        totalRecords: total,
        totalPages: Math.max(1, Math.ceil(total / this.perPage)),
        currentPage: this.currentPage,
        perPage: this.perPage,
      };
    },
    countLabel() {
      const total = this.filteredPayments.length;
      return total === 1 ? "1 payment" : `${total} payments`;
    },
    emptyMessage() {
      if (this.statusFilter && this.payments.length) {
        return `No ${this.statusLabel(this.statusFilter).toLowerCase()} payments in this view.`;
      }
      return this.hasFilter ? "No payments in the selected date range." : "No payments recorded yet.";
    },
    hasFilter() {
      return !!(this.appliedStart && this.appliedEnd);
    },
    canApply() {
      return !!(this.startDate && this.endDate);
    },
    rangeLabel() {
      if (!this.hasFilter) return "All time";
      return `${this.$filters.getDateOnly(this.appliedStart)} → ${this.$filters.getDateOnly(this.appliedEnd)}`;
    },
    rangeChip() {
      return this.hasFilter ? "in range" : "all time";
    },
    // The dominant currency across the loaded payments, used to total amounts.
    primaryCurrency() {
      const freq = {};
      for (const p of this.payments) {
        const c = p.currency || DEFAULT_CURRENCY;
        freq[c] = (freq[c] || 0) + 1;
      }
      let best = DEFAULT_CURRENCY;
      let max = -1;
      for (const c of Object.keys(freq)) {
        if (freq[c] > max) { max = freq[c]; best = c; }
      }
      return best;
    },
    successfulPayments() {
      return this.payments.filter((p) => p.status === PAYMENT_STATUS.success);
    },
    totalCollected() {
      return this.successfulPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    },
    collectedCount() {
      return this.successfulPayments.length;
    },
    totalCount() {
      return this.payments.length;
    },
    pendingCount() {
      return this.payments.filter((p) => p.status === PAYMENT_STATUS.pending).length;
    },
    pendingAmount() {
      return this.payments
        .filter((p) => p.status === PAYMENT_STATUS.pending)
        .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    },
    failedCount() {
      return this.payments.filter(
        (p) => p.status === PAYMENT_STATUS.failed || p.status === PAYMENT_STATUS.cancelled,
      ).length;
    },
  },
  methods: {
    fmt(n) {
      return Number(n || 0).toLocaleString();
    },
    money(value, currency) {
      const amount = Number(value);
      if (isNaN(amount)) return "—";
      return this.$filters.formatPrice(amount, currency || this.primaryCurrency);
    },
    rowNumber(index) {
      return (this.currentPage - 1) * this.perPage + index + 1;
    },
    // The account a payment belongs to = the organization's owner (a user).
    accountName(payment) {
      const owner = payment.organization && payment.organization.user ? payment.organization.user : null;
      if (owner) return this.$filters.fullName(owner.fname, owner.lname) || "—";
      if (payment.createdByUser) {
        return this.$filters.fullName(payment.createdByUser.fname, payment.createdByUser.lname) || "—";
      }
      return payment.organization && payment.organization.name ? payment.organization.name : "—";
    },
    statusLabel(status) {
      return status ? status.charAt(0).toUpperCase() + status.slice(1) : "—";
    },
    statusBadgeClass(status) {
      return STATUS_BADGE[status] || "badge-secondary";
    },
    async loadPayments() {
      this.isLoading = true;
      try {
        // Load the full set for the active range so the stats are exact.
        const params = { limit: 100000 };
        if (this.appliedStart && this.appliedEnd) {
          params.startDate = `${this.appliedStart}T00:00:00`;
          params.endDate = `${this.appliedEnd}T23:59:59`;
        }
        const res = await Payment.getPayments(params);
        if (res?.data?.status) {
          this.payments = res.data.data || [];
          this.currentPage = 1;
        }
      } finally {
        this.isLoading = false;
      }
    },
    applyFilter() {
      if (!this.canApply) return;
      if (new Date(this.startDate) > new Date(this.endDate)) {
        this.$toast.error("The start date must be before the end date.", TOAST_GLOBAL_CONFIG);
        return;
      }
      this.appliedStart = this.startDate;
      this.appliedEnd = this.endDate;
      this.loadPayments();
    },
    clearFilter() {
      this.startDate = "";
      this.endDate = "";
      this.appliedStart = "";
      this.appliedEnd = "";
      this.statusFilter = "";
      this.loadPayments();
    },
    onStatusFilterChange() {
      // Client-side refinement only; no reload needed.
      this.currentPage = 1;
    },
    onPageChange({ page, perPage }) {
      this.currentPage = page;
      this.perPage = perPage;
    },
    viewPayment(payment) {
      this.selectedPayment = payment;
      this.showDetailModal = true;
    },
  },
  mounted() {
    this.loadPayments();
  },
};
</script>
<style scoped>
/* Stat cards */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.stat-card {
  position: relative;
  overflow: hidden;
  padding: 16px;
  border: 1px solid var(--ehs-color-border);
  background: var(--ehs-color-surface);
  border-radius: var(--ehs-radius-lg);
  box-shadow: var(--ehs-shadow-card);
}

.stat-card::before {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 3px;
  background: var(--ehs-color-primary);
}

.stat-card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 36px;
}

.stat-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  font-size: 0.95rem;
  background: var(--ehs-color-primary-soft);
  color: var(--ehs-color-primary);
}

.stat-icon.indigo { background: var(--ehs-color-primary-soft); color: var(--ehs-color-primary); }
.stat-icon.green { background: #f0fdf4; color: var(--ehs-color-success); }
.stat-icon.amber { background: #fffbeb; color: var(--ehs-color-warning); }
.stat-icon.red { background: var(--ehs-color-danger-soft); color: var(--ehs-color-danger); }

.stat-chip {
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--ehs-color-muted);
  background: var(--ehs-color-canvas);
  border-radius: var(--ehs-radius-pill);
  padding: 3px 9px;
  text-transform: capitalize;
}

.stat-card h2 {
  margin: 14px 0 2px;
  color: var(--ehs-color-ink);
  font-family: var(--ehs-font-display);
  font-size: 1.55rem;
  font-weight: 600;
  letter-spacing: -0.025em;
}

.stat-card h2.amount {
  font-size: 1.35rem;
}

.stat-card p {
  margin: 0;
  color: var(--ehs-color-muted);
  font-size: 0.78rem;
  font-weight: 600;
}

.stat-sub {
  display: block;
  margin-top: 6px;
  font-size: 0.72rem;
  color: var(--ehs-color-soft);
}

/* Date filter */
.payments-filter {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-field label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--ehs-color-soft);
}

.filter-field .form-control {
  width: auto;
  min-width: 160px;
}

.filter-apply {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 16px;
  border: 1px solid var(--ehs-color-primary);
  border-radius: var(--ehs-radius-md);
  background: var(--ehs-color-primary);
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.filter-apply:hover:not(:disabled) {
  background: var(--ehs-color-primary-hover);
  border-color: var(--ehs-color-primary-hover);
}

.filter-apply:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.filter-apply .pi {
  font-size: 0.72rem;
}

.filter-clear {
  padding: 9px 14px;
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-md);
  background: var(--ehs-color-surface);
  color: var(--ehs-color-muted);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.filter-clear:hover:not(:disabled) {
  background: var(--ehs-color-canvas);
  color: var(--ehs-color-ink);
}

.amount-cell {
  font-weight: 600;
  color: var(--ehs-color-ink);
}

.ref-text {
  font-family: var(--ehs-font-mono);
  font-size: 0.78rem;
  color: var(--ehs-color-text);
}

.view-btn {
  border: 0;
  background: transparent;
  padding: 4px 4px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--ehs-color-primary-active);
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.15s ease;
}

.view-btn:hover {
  color: var(--ehs-color-primary-hover);
  text-decoration: underline;
}

.view-btn:focus-visible {
  outline: none;
  text-decoration: underline;
}

@media (max-width: 1200px) {
  .stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 520px) {
  .stats-grid { grid-template-columns: 1fr; }
}
</style>
